import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type LensPlusView = "monthly" | "day" | "all-time";

export const useLensPlusMonth = (): {
    month: string;
    options: { value: string; label: string }[];
    setMonth: (value: string, view?: LensPlusView) => boolean;
    view: LensPlusView;
    setView: (value: LensPlusView) => void;
} => {
    const [params, setParams] = useSearchParams();
    const current = new Date().toISOString().slice(0, 7);
    const [initialMonth] = useState(current);
    const requested = params.get("month");
    const validMonth = (value: string) =>
        /^\d{4}-(0[1-9]|1[0-2])$/.test(value) && value >= "2000-01" && value <= current;
    const month = requested && validMonth(requested) ? requested : initialMonth;
    const options = useMemo(() => {
        const now = new Date(`${current}-01T00:00:00Z`);
        const dates = Array.from(
            { length: 12 },
            (_, offset) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1)),
        );
        if (!dates.some((date) => date.toISOString().slice(0, 7) === month))
            dates.push(new Date(`${month}-01T00:00:00Z`));
        return dates.map((date) => ({
            value: date.toISOString().slice(0, 7),
            label: date.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }),
        }));
    }, [current, month]);
    const view: LensPlusView =
        params.get("view") === "all-time" ? "all-time" : params.get("view") === "day" ? "day" : "monthly";
    const setMonth = (value: string, nextView?: LensPlusView) => {
        if (!validMonth(value) || (!nextView && !options.some((option) => option.value === value))) return false;
        setParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.set("month", value);
                if (nextView) next.set("view", nextView);
                return next;
            },
            { preventScrollReset: true },
        );
        return true;
    };
    const setView = (value: LensPlusView) => {
        setMonth(month, value);
    };
    return { month, options, setMonth, view, setView };
};
